// src/hooks/useAddresses.js
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const API_URL = import.meta.env.VITE_API_URL;
const STORAGE_KEY = 'userAddresses';
const SELECTED_KEY = 'selectedAddress';

// Two saved addresses within this many meters are treated as the same place.
// Mapbox returns identical street names for distinct coordinates in pueblos,
// so dedup must be geographic, not string-based.
const SAME_LOCATION_THRESHOLD_M = 10;

const toLngLat = (coords) => {
  if (!coords) return null;
  if (Array.isArray(coords)) return coords; // [lng, lat] (MongoDB)
  if (typeof coords.lng === 'number' && typeof coords.lat === 'number') {
    return [coords.lng, coords.lat];
  }
  return null;
};

const haversineDistanceMeters = (a, b) => {
  const [lng1, lat1] = a;
  const [lng2, lat2] = b;
  const R = 6371000;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;
  const h = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

const findDuplicateAddress = (list, candidate) => {
  const cLngLat = toLngLat(candidate.coordinates);
  if (!cLngLat) return null;
  return list.find((existing) => {
    const eLngLat = toLngLat(existing.coordinates);
    if (!eLngLat) return false;
    return haversineDistanceMeters(eLngLat, cLngLat) <= SAME_LOCATION_THRESHOLD_M;
  }) || null;
};

/**
 * Hook para gestionar direcciones del usuario
 * - Si está autenticado: sincroniza con backend
 * - Si no está autenticado: usa localStorage
 */
export const useAddresses = () => {
  const { isAuthenticated, token } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [synced, setSynced] = useState(false);

  // Headers para peticiones autenticadas
  const authHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  }), [token]);

  // ══════════════════════════════════════════════════════════
  // CARGAR DIRECCIONES
  // ══════════════════════════════════════════════════════════

  const loadAddresses = useCallback(async () => {
    if (isAuthenticated && token) {
      // Cargar desde backend
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/me/addresses`, {
          headers: authHeaders()
        });
        if (res.ok) {
          const data = await res.json();
          setAddresses(data);

          // Seleccionar la default o la primera
          const defaultAddr = data.find(a => a.isDefault) || data[0];
          if (defaultAddr) {
            setSelectedAddress(defaultAddr.street);
          }
          setSynced(true);
        }
      } catch (error) {
        console.error('Error loading addresses from backend:', error);
        // Fallback a localStorage
        loadFromLocalStorage();
      } finally {
        setLoading(false);
      }
    } else {
      // Cargar desde localStorage
      loadFromLocalStorage();
    }
  }, [isAuthenticated, token, authHeaders]);

  const loadFromLocalStorage = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const selected = localStorage.getItem(SELECTED_KEY);
      if (stored) {
        setAddresses(JSON.parse(stored));
      }
      if (selected) {
        setSelectedAddress(selected);
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
    }
  };

  // Cargar al montar y cuando cambie autenticación
  useEffect(() => {
    loadAddresses();
  }, [loadAddresses]);

  // Sincronizar todas las instancias de useAddresses cuando una hace cambios:
  //  - 'addressesChanged' (add/remove/update) → full reload de la lista
  //  - 'addressSelectionChanged' (select) → update directo del selectedAddress
  //    sin refetch (más eficiente, evita race con backend al marcar default)
  useEffect(() => {
    const handleAddressesChanged = () => loadAddresses();
    const handleSelectionChanged = (e) => {
      if (e.detail?.street) setSelectedAddress(e.detail.street);
    };
    window.addEventListener('addressesChanged', handleAddressesChanged);
    window.addEventListener('addressSelectionChanged', handleSelectionChanged);
    return () => {
      window.removeEventListener('addressesChanged', handleAddressesChanged);
      window.removeEventListener('addressSelectionChanged', handleSelectionChanged);
    };
  }, [loadAddresses]);

  // ══════════════════════════════════════════════════════════
  // SINCRONIZAR localStorage AL BACKEND (cuando el usuario se autentica)
  // ══════════════════════════════════════════════════════════

  useEffect(() => {
    const syncLocalToBackend = async () => {
      if (!isAuthenticated || !token || synced) return;

      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return;

        const localAddresses = JSON.parse(stored);
        if (localAddresses.length === 0) {
          localStorage.removeItem(STORAGE_KEY);
          return;
        }

        // Fetch backend list once to avoid uploading near-duplicates that
        // already exist server-side (e.g., user logged out, re-added the
        // same place locally, then logged back in).
        let backendList = [];
        try {
          const res = await fetch(`${API_URL}/api/me/addresses`, { headers: authHeaders() });
          if (res.ok) backendList = await res.json();
        } catch (e) {
          console.error('Error fetching backend addresses for sync:', e);
        }

        const toUpload = localAddresses.filter(
          (addr) => !findDuplicateAddress(backendList, addr)
        );

        for (const addr of toUpload) {
          try {
            await fetch(`${API_URL}/api/me/addresses`, {
              method: 'POST',
              headers: authHeaders(),
              body: JSON.stringify({
                label: addr.label || 'Casa',
                street: addr.street,
                reference: addr.reference || '',
                coordinates: addr.coordinates,
                isDefault: false
              })
            });
          } catch (e) {
            console.error('Error syncing address:', e);
          }
        }

        localStorage.removeItem(STORAGE_KEY);
        loadAddresses();
      } catch (error) {
        console.error('Error syncing to backend:', error);
      }
    };

    syncLocalToBackend();
  }, [isAuthenticated, token, synced, authHeaders, loadAddresses]);

  // ══════════════════════════════════════════════════════════
  // AGREGAR DIRECCIÓN
  // ══════════════════════════════════════════════════════════

  const addAddress = useCallback(async (addressData) => {
    const { street, reference = '', coordinates, label = 'Casa' } = addressData;

    if (!street || !coordinates) {
      console.error('addAddress: street y coordinates son requeridos');
      return null;
    }

    // Upsert by geographic proximity: if the user is saving a point within
    // SAME_LOCATION_THRESHOLD_M of an existing address, treat it as the same
    // place. Patch label/reference if they changed; otherwise return as-is.
    const duplicate = findDuplicateAddress(addresses, { coordinates });
    if (duplicate) {
      const labelChanged = label && label !== duplicate.label;
      const refChanged = reference && reference !== (duplicate.reference || '');
      if (!labelChanged && !refChanged) return duplicate;

      const patch = {
        ...(labelChanged ? { label } : {}),
        ...(refChanged ? { reference } : {}),
      };

      if (isAuthenticated && token) {
        try {
          const res = await fetch(`${API_URL}/api/me/addresses/${duplicate._id}`, {
            method: 'PUT',
            headers: authHeaders(),
            body: JSON.stringify(patch),
          });
          if (res.ok) {
            const saved = await res.json();
            setAddresses(prev => prev.map(a => (a._id === duplicate._id ? saved : a)));
            window.dispatchEvent(new CustomEvent('addressesChanged'));
            return saved;
          }
        } catch (error) {
          console.error('Error patching duplicate address:', error);
        }
        return duplicate;
      }

      const merged = { ...duplicate, ...patch };
      const updated = addresses.map(a => (a._id === duplicate._id ? merged : a));
      setAddresses(updated);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      window.dispatchEvent(new CustomEvent('addressesChanged'));
      return merged;
    }

    const newAddress = {
      label,
      street,
      reference,
      coordinates: [coordinates.lng, coordinates.lat], // MongoDB format
      isDefault: addresses.length === 0
    };

    if (isAuthenticated && token) {
      // Guardar en backend
      try {
        const res = await fetch(`${API_URL}/api/me/addresses`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify(newAddress)
        });

        if (res.ok) {
          const saved = await res.json();
          setAddresses(prev => [...prev, saved]);
          setSelectedAddress(saved.street);
          window.dispatchEvent(new CustomEvent('addressesChanged'));
          return saved;
        }
      } catch (error) {
        console.error('Error adding address to backend:', error);
      }
    } else {
      // Guardar en localStorage
      const localAddress = {
        _id: `local_${Date.now()}`,
        ...newAddress,
        createdAt: new Date().toISOString()
      };

      const updated = [...addresses, localAddress];
      setAddresses(updated);
      setSelectedAddress(localAddress.street);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      localStorage.setItem(SELECTED_KEY, localAddress.street);
      window.dispatchEvent(new CustomEvent('addressesChanged'));
      return localAddress;
    }

    return null;
  }, [addresses, isAuthenticated, token, authHeaders]);

  // ══════════════════════════════════════════════════════════
  // ELIMINAR DIRECCIÓN
  // ══════════════════════════════════════════════════════════

  const removeAddress = useCallback(async (addressId) => {
    if (isAuthenticated && token) {
      try {
        const res = await fetch(`${API_URL}/api/me/addresses/${addressId}`, {
          method: 'DELETE',
          headers: authHeaders()
        });

        if (res.ok) {
          setAddresses(prev => prev.filter(a => a._id !== addressId));
          // Si era la seleccionada, seleccionar otra
          const removed = addresses.find(a => a._id === addressId);
          if (removed && removed.street === selectedAddress) {
            const remaining = addresses.filter(a => a._id !== addressId);
            setSelectedAddress(remaining[0]?.street || null);
          }
          window.dispatchEvent(new CustomEvent('addressesChanged'));
        }
      } catch (error) {
        console.error('Error removing address:', error);
      }
    } else {
      // Eliminar de localStorage
      const updated = addresses.filter(a => a._id !== addressId);
      setAddresses(updated);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));

      const removed = addresses.find(a => a._id === addressId);
      if (removed && removed.street === selectedAddress) {
        const newSelected = updated[0]?.street || null;
        setSelectedAddress(newSelected);
        if (newSelected) {
          localStorage.setItem(SELECTED_KEY, newSelected);
        } else {
          localStorage.removeItem(SELECTED_KEY);
        }
      }
      window.dispatchEvent(new CustomEvent('addressesChanged'));
    }
  }, [addresses, selectedAddress, isAuthenticated, token, authHeaders]);

  // ══════════════════════════════════════════════════════════
  // ACTUALIZAR DIRECCIÓN
  // ══════════════════════════════════════════════════════════

  const updateAddress = useCallback(async (addressId, updates) => {
    const updateData = { ...updates };
    if (updates.coordinates && !Array.isArray(updates.coordinates)) {
      updateData.coordinates = [updates.coordinates.lng, updates.coordinates.lat];
    }

    if (isAuthenticated && token) {
      try {
        const res = await fetch(`${API_URL}/api/me/addresses/${addressId}`, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify(updateData)
        });
        if (res.ok) {
          const saved = await res.json();
          setAddresses(prev => prev.map(a => (a._id === addressId ? saved : a)));
          window.dispatchEvent(new CustomEvent('addressesChanged'));
          if (saved.street && updates.street) {
            // If street was renamed, update selectedAddress reference
            const prevStreet = addresses.find(a => a._id === addressId)?.street;
            if (prevStreet === selectedAddress) {
              setSelectedAddress(saved.street);
            }
          }
          return saved;
        }
      } catch (error) {
        console.error('Error updating address in backend:', error);
      }
      return null;
    } else {
      // localStorage
      const updated = addresses.map(a =>
        a._id === addressId ? { ...a, ...updateData } : a
      );
      setAddresses(updated);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      const newAddr = updated.find(a => a._id === addressId);
      // If street name changed and was the selected one, update selection
      const prevAddr = addresses.find(a => a._id === addressId);
      if (prevAddr && prevAddr.street === selectedAddress && newAddr && newAddr.street !== prevAddr.street) {
        setSelectedAddress(newAddr.street);
        localStorage.setItem(SELECTED_KEY, newAddr.street);
      }
      window.dispatchEvent(new CustomEvent('addressesChanged'));
      return newAddr || null;
    }
  }, [addresses, selectedAddress, isAuthenticated, token, authHeaders]);

  // ══════════════════════════════════════════════════════════
  // SELECCIONAR DIRECCIÓN
  // ══════════════════════════════════════════════════════════

  const selectAddress = useCallback(async (street) => {
    setSelectedAddress(street);

    if (isAuthenticated && token) {
      // Marcar como default en backend
      const address = addresses.find(a => a.street === street);
      if (address && address._id) {
        try {
          await fetch(`${API_URL}/api/me/addresses/${address._id}/default`, {
            method: 'PUT',
            headers: authHeaders()
          });
        } catch (error) {
          console.error('Error setting default address:', error);
        }
      }
    } else {
      localStorage.setItem(SELECTED_KEY, street);
    }

    // Notifica a OTRAS instancias de useAddresses (ej. la del Checkout cuando
    // el usuario seleccionó desde el LocationModal) para que actualicen su
    // selectedAddress sin necesidad de refetch del backend.
    window.dispatchEvent(new CustomEvent('addressSelectionChanged', { detail: { street } }));
  }, [addresses, isAuthenticated, token, authHeaders]);

  // ══════════════════════════════════════════════════════════
  // OBTENER OBJETO DE DIRECCIÓN SELECCIONADA
  // ══════════════════════════════════════════════════════════

  const getSelectedAddressObject = useCallback(() => {
    if (!selectedAddress) return null;
    return addresses.find(a => a.street === selectedAddress) || null;
  }, [addresses, selectedAddress]);

  // ══════════════════════════════════════════════════════════
  // RETURN
  // ══════════════════════════════════════════════════════════

  return {
    addresses,
    selectedAddress,
    loading,
    addAddress,
    updateAddress,
    removeAddress,
    selectAddress,
    getSelectedAddressObject,
    refreshAddresses: loadAddresses
  };
};

export default useAddresses;
